import React, { useEffect, useMemo, useRef, useState } from 'react';
import './GalleryPaginationIndicators.scss';
import RecircArrowIcon from './recirc-arrow.svg?react';
import RecircArrowActiveIcon from './recirc-arrow-active.svg?react';

export interface GalleryPaginationIndicatorsProps {
  count: number;
  currentIndex: number;
  activeSlideAutoplayProgress: number;
  isAutoplayPaused?: boolean;
  onDotClick: (index: number) => void;
  className?: string;
}

export const GalleryPaginationIndicators: React.FC<GalleryPaginationIndicatorsProps> = ({
                                                                        count,
                                                                        currentIndex,
                                                                        activeSlideAutoplayProgress,
                                                                        isAutoplayPaused = false,
                                                                        onDotClick,
                                                                        className = '',
                                                                      }) => {
  const dotRefs = useRef<(HTMLButtonElement | null)[]>([]); // Array to store refs for each dot
  const containerRef = useRef<HTMLDivElement | null>(null); // Ref for the container
  const isArrowActive = useMemo(() => currentIndex === count - 1, [currentIndex, count]);

  useEffect(() => {
    dotRefs.current = dotRefs.current.slice(0, count);
  }, [count]);

  if (count <= 0) {
    return null;
  }


  return (
    <div
      ref={containerRef}
      className={`custom-pagination-container ${className}`}
      role="tablist"
      aria-label="Gallery pagination"
    >
      {Array.from({ length: count-1 }).map((_, i) => (
        <button
          key={i}
          ref={(el) => (dotRefs.current[i] = el)} // Assign ref to each dot
          role="tab"
          type="button"
          className={`custom-pagination-dot ${i === currentIndex ? 'active' : ''}`}
          onClick={() => onDotClick(i)}
          aria-label={`Go to slide ${i + 1}`}
          aria-selected={i === currentIndex}
        >
          {i === currentIndex && (
            <span
              className={`custom-pagination-progress ${isAutoplayPaused ? 'paused' : ''}`}
              style={{ transform: `scaleX(${activeSlideAutoplayProgress})` }}
            />
          )}
        </button>
      ))}
      <button
        role="tab"
        type="button"
        className={`custom-pagination-arrow ${isArrowActive ? 'active' : ''}`}
        aria-label="Next slide"
        aria-selected={isArrowActive}
        onClick={() => onDotClick(count - 1)}
      >
        <RecircArrowActiveIcon
          className={`arrow-svg ${isArrowActive ? 'visible' : 'hidden'}`}
          aria-hidden={!isArrowActive}
        />
        <RecircArrowIcon
          className={`arrow-svg ${!isArrowActive ? 'visible' : 'hidden'}`}
          aria-hidden={isArrowActive}
        />
      </button>
    </div>
  );
};
